import React, { useRef, useState, useEffect, useCallback, useImperativeHandle } from 'react';
import * as d3 from 'd3-hierarchy';
import { select } from 'd3-selection';
import { zoom as d3Zoom, zoomIdentity } from 'd3-zoom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { Slider } from 'primereact/slider';
import { InputText } from 'primereact/inputtext';
import { SelectButton } from 'primereact/selectbutton';
import { ProgressSpinner } from 'primereact/progressspinner';
import { orgChartService } from '../../services/orgChartService';

/**
 * Professional OrgChartTree Component with Draw.io-style Canvas
 * Features: Smooth zoom slider, proper panning, large readable nodes, path navigation, lazy loading
 */
const OrgChartTree = ({
    treeData,
    onPositionClick,
    onAddChild,
    onAddSibling,
    onEditPosition,
    onDeletePosition,
    onAssignUser,
    onRemoveUser,
    loading = false,
    isAdmin = false,
    highlightedPath = [],  // Array of position IDs to highlight (from search)
    selectedPositionId = null,  // Currently selected position ID (from search)
    onSearchResultClick,  // Callback for search result navigation
    toast,  // Toast ref for notifications
    onLoadChildren,  // Lazy loading: callback to load children
    expandingPositionId = null,  // Lazy loading: position ID currently loading children
    expandNodeRef  // Lazy loading: ref to expose expandNode function
}) => {
    const svgRef = useRef(null);
    const containerRef = useRef(null);
    const contextMenuRef = useRef(null);
    const zoomBehaviorRef = useRef(null);
    const currentTransformRef = useRef(null);
    const [hoveredNode, setHoveredNode] = useState(null);
    const [dimensions, setDimensions] = useState({ width: 1200, height: 800 });
    const [menuItems, setMenuItems] = useState([]);
    const [zoomLevel, setZoomLevel] = useState(100); // 10% to 200%
    const [expandedNodes, setExpandedNodes] = useState(new Set());
    const isFirstLoadRef = useRef(true); // Track if it's the first tree load

    // Search Panel State
    const [searchPanelOpen, setSearchPanelOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchType, setSearchType] = useState('position');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchDebounceTimeout, setSearchDebounceTimeout] = useState(null);

    // Constants for tree layout - Professional sizes
    const NODE_WIDTH = 260;
    const NODE_HEIGHT = 150;
    const HORIZONTAL_SPACING = 320;
    const VERTICAL_SPACING = 200;
    const ROOT_SPACING = 400;

    // Initialize expanded nodes to show only 2 levels (root + direct children)
    const initializeExpandedNodes = (nodes) => {
        const expanded = new Set();

        const traverse = (node, depth = 0) => {
            // Add root node (Level 0) - always expand roots
            expanded.add(node.id);

            // ONLY add direct children IF they have loaded children
            // Don't add nodes with has_children=true but empty children array (lazy loading)
            if (depth === 0 && node.children && node.children.length > 0) {
                node.children.forEach(child => {
                    // Only add to expanded set if child has loaded children
                    if (child.children && child.children.length > 0) {
                        expanded.add(child.id);
                    }
                });
            }

            // Continue traversing but don't expand beyond level 1
            if (node.children) {
                node.children.forEach(child => traverse(child, depth + 1));
            }
        };

        // Process all root nodes
        if (Array.isArray(nodes)) {
            nodes.forEach(rootNode => traverse(rootNode));
        }

        return expanded;
    };

    // Find node by ID in tree data
    const findNodeById = (id) => {
        const searchNode = (node) => {
            if (node.id === id) return node;
            if (node.children) {
                for (const child of node.children) {
                    const found = searchNode(child);
                    if (found) return found;
                }
            }
            return null;
        };

        if (Array.isArray(treeData) && treeData.length > 0) {
            for (const rootNode of treeData) {
                const found = searchNode(rootNode);
                if (found) return found;
            }
        }

        return null;
    };

    // Toggle node expansion (with lazy loading support)
    // MATCHES MLM IMPLEMENTATION EXACTLY
    const toggleNodeExpand = useCallback((nodeId, nodeData) => {
        if (!nodeData) {
            console.warn('toggleNodeExpand: nodeData is required');
            return;
        }

        // Check if children need to be loaded (lazy loading)
        if (nodeData.has_children && (!nodeData.children || nodeData.children.length === 0)) {
            // Children NOT loaded yet → Trigger lazy load
            // DON'T expand yet - parent will expand AFTER data loads
            console.log('OrgChart: Node has children but not loaded, triggering lazy load for:', nodeId);
            if (onLoadChildren) {
                onLoadChildren(nodeId);
            }
        } else {
            // Children already loaded OR no children → Just toggle visibility
            setExpandedNodes(prev => {
                const newSet = new Set(prev);
                if (newSet.has(nodeId)) {
                    console.log('OrgChart: Collapsing node:', nodeId);
                    newSet.delete(nodeId);
                } else {
                    console.log('OrgChart: Expanding node:', nodeId);
                    newSet.add(nodeId);
                }
                return newSet;
            });
        }
    }, [onLoadChildren]);

    // Expand node programmatically (used after lazy loading completes)
    const expandNode = useCallback((nodeId) => {
        console.log('expandNode called for:', nodeId);
        setExpandedNodes(prev => {
            const newSet = new Set(prev);
            newSet.add(nodeId);
            return newSet;
        });
    }, []);

    // Expose expandNode function to parent via ref
    useImperativeHandle(expandNodeRef, () => expandNode, [expandNode]);

    // Search functionality
    const handleSearchQueryChange = useCallback((e) => {
        const query = e.target.value;
        setSearchQuery(query);

        // Clear existing timeout
        if (searchDebounceTimeout) {
            clearTimeout(searchDebounceTimeout);
        }

        // Don't search if query is less than 2 characters
        if (query.length < 2) {
            setSearchResults([]);
            return;
        }

        // Set new timeout for debouncing (300ms delay)
        const timeout = setTimeout(() => {
            performSearch(query, searchType);
        }, 300);

        setSearchDebounceTimeout(timeout);
    }, [searchDebounceTimeout, searchType]);

    const performSearch = async (query, type) => {
        if (query.length < 2) {
            setSearchResults([]);
            return;
        }

        setSearchLoading(true);
        try {
            const response = await orgChartService.searchPositions(query, type, 10);

            if (response.success) {
                setSearchResults(response.data.results || []);
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Search Error',
                    detail: response.message || 'Failed to search positions',
                    life: 3000
                });
                setSearchResults([]);
            }
        } catch (error) {
            console.error('Search error:', error);
            toast?.current?.show({
                severity: 'error',
                summary: 'Search Error',
                detail: error.message || 'Failed to search positions',
                life: 3000
            });
            setSearchResults([]);
        } finally {
            setSearchLoading(false);
        }
    };

    const handleSearchTypeChange = (e) => {
        const newType = e.value;
        setSearchType(newType);

        // Re-trigger search if there's a query
        if (searchQuery.length >= 2) {
            performSearch(searchQuery, newType);
        }
    };

    const handleSearchResultSelect = async (position) => {
        if (onSearchResultClick) {
            await onSearchResultClick(position);
        }
        // Close search panel after selection
        setSearchPanelOpen(false);
        setSearchQuery('');
        setSearchResults([]);
    };

    const toggleSearchPanel = () => {
        setSearchPanelOpen(!searchPanelOpen);
        if (!searchPanelOpen) {
            // Reset search when opening
            setSearchQuery('');
            setSearchResults([]);
            setSearchType('position');
        }
    };

    // Cleanup search timeout on unmount
    useEffect(() => {
        return () => {
            if (searchDebounceTimeout) {
                clearTimeout(searchDebounceTimeout);
            }
        };
    }, [searchDebounceTimeout]);

    useEffect(() => {
        if (containerRef.current) {
            const { width, height } = containerRef.current.getBoundingClientRect();
            setDimensions({ width: width || 1200, height: height || 800 });
        }
    }, []);

    // Initialize expanded nodes ONLY on first load
    // NEVER reset on subsequent tree updates (create/edit/delete operations)
    useEffect(() => {
        if (treeData && treeData.length > 0 && isFirstLoadRef.current) {
            setExpandedNodes(initializeExpandedNodes(treeData));
            isFirstLoadRef.current = false; // Mark as initialized - never initialize again
        }
    }, [treeData]);

    // Auto-expand path nodes when search navigation occurs
    useEffect(() => {
        if (highlightedPath && highlightedPath.length > 0) {
            setExpandedNodes(prev => {
                const newSet = new Set(prev);
                // Expand all nodes in the path to make the target visible
                highlightedPath.forEach(positionId => {
                    newSet.add(positionId);
                });
                return newSet;
            });
        }
    }, [highlightedPath]);

    useEffect(() => {
        if (!treeData || treeData.length === 0 || !svgRef.current) return;
        renderTree();
    }, [treeData, dimensions, expandedNodes]);

    useEffect(() => {
        if (!svgRef.current || !treeData || treeData.length === 0) return;
        updateNodeAppearance();
    }, [hoveredNode, highlightedPath, selectedPositionId, expandingPositionId]);

    const getContextMenuItems = (nodeData) => {
        const hasUsers = nodeData.active_users && nodeData.active_users.length > 0;
        const items = [];

        if (isAdmin) {
            items.push({
                label: 'Add Child Position',
                icon: 'pi pi-plus-circle',
                command: () => onAddChild && onAddChild(nodeData)
            });

            if (nodeData.parent_position_id) {
                items.push({
                    label: 'Add Sibling Position',
                    icon: 'pi pi-plus',
                    command: () => onAddSibling && onAddSibling(nodeData)
                });
            }

            items.push({ separator: true });

            if (hasUsers) {
                items.push({
                    label: 'View User Details',
                    icon: 'pi pi-user',
                    command: () => onPositionClick && onPositionClick(nodeData)
                });
                items.push({
                    label: 'Remove User',
                    icon: 'pi pi-user-minus',
                    command: () => onRemoveUser && onRemoveUser(nodeData)
                });
            } else {
                items.push({
                    label: 'Assign User',
                    icon: 'pi pi-user-plus',
                    command: () => onAssignUser && onAssignUser(nodeData)
                });
            }

            items.push({ separator: true });

            items.push({
                label: 'Edit Position',
                icon: 'pi pi-pencil',
                command: () => onEditPosition && onEditPosition(nodeData)
            });

            items.push({
                label: 'Delete Position',
                icon: 'pi pi-trash',
                className: 'text-red-500',
                command: () => onDeletePosition && onDeletePosition(nodeData)
            });
        } else {
            items.push({
                label: 'View Details',
                icon: 'pi pi-eye',
                command: () => onPositionClick && onPositionClick(nodeData)
            });
        }

        return items;
    };

    const updateNodeAppearance = () => {
        const svg = select(svgRef.current);
        const nodes = svg.selectAll('.node-content');
        nodes.html(d => renderNodeHTML(d));
    };

    const renderTree = () => {
        const svg = select(svgRef.current);
        svg.selectAll('*').remove();

        svg.attr('width', dimensions.width)
           .attr('height', dimensions.height);

        const g = svg.append('g').attr('class', 'zoom-group');

        // Setup smooth zoom and pan behavior
        const zoomBehavior = d3Zoom()
            .scaleExtent([0.1, 2])
            .filter((event) => {
                // Allow mouse wheel zoom and drag panning
                // Prevent zoom on dblclick, allow all other events
                return !event.button && event.type !== 'dblclick';
            })
            .on('zoom', (event) => {
                currentTransformRef.current = event.transform;
                setZoomLevel(Math.round(event.transform.k * 100));
                g.attr('transform', event.transform);
            });

        zoomBehaviorRef.current = zoomBehavior;
        svg.call(zoomBehavior);

        // Process each root tree separately - only show children of expanded nodes
        const rootTrees = treeData.map(rootData => d3.hierarchy(rootData, d => {
            if (expandedNodes.has(d.id)) {
                return d.children; // Show children if expanded
            }
            return null; // Hide children if collapsed
        }));

        const treeLayout = d3.tree()
            .nodeSize([HORIZONTAL_SPACING, VERTICAL_SPACING])
            .separation((a, b) => a.parent === b.parent ? 1 : 1.5);

        rootTrees.forEach(root => treeLayout(root));

        let currentXOffset = 0;
        const allNodes = [];
        const allLinks = [];

        rootTrees.forEach((root) => {
            let minX = Infinity, maxX = -Infinity;
            root.each(node => {
                if (node.x < minX) minX = node.x;
                if (node.x > maxX) maxX = node.x;
            });

            const treeWidth = maxX - minX;
            const xOffset = currentXOffset - minX + NODE_WIDTH / 2 + 100;

            root.each(node => {
                node.displayX = node.x + xOffset;
                node.displayY = node.y + NODE_HEIGHT / 2 + 100;
                allNodes.push(node);
            });

            allLinks.push(...root.links());
            currentXOffset += treeWidth + ROOT_SPACING;
        });

        // Restore or set initial transform
        if (currentTransformRef.current) {
            // Restore previous transform
            svg.call(zoomBehavior.transform, currentTransformRef.current);
        } else {
            // Set initial transform
            const initialScale = 0.8;
            const initialTransform = zoomIdentity
                .translate(dimensions.width * 0.05, 40)
                .scale(initialScale);
            currentTransformRef.current = initialTransform;
            setZoomLevel(Math.round(initialScale * 100));
            svg.call(zoomBehavior.transform, initialTransform);
        }

        // Draw connecting lines
        g.selectAll('.link')
            .data(allLinks)
            .join('path')
            .attr('class', 'link')
            .attr('d', d => {
                const sourceX = d.source.displayX;
                const sourceY = d.source.displayY + NODE_HEIGHT / 2;
                const targetX = d.target.displayX;
                const targetY = d.target.displayY - NODE_HEIGHT / 2;

                return `
                    M ${sourceX},${sourceY}
                    V ${sourceY + 60}
                    H ${targetX}
                    V ${targetY}
                `;
            })
            .attr('fill', 'none')
            .attr('stroke', '#94a3b8')
            .attr('stroke-width', 2);

        // Draw nodes
        const nodeGroups = g.selectAll('.node')
            .data(allNodes)
            .join('g')
            .attr('class', 'node')
            .attr('transform', d => `translate(${d.displayX - NODE_WIDTH / 2}, ${d.displayY - NODE_HEIGHT / 2})`);

        nodeGroups.append('foreignObject')
            .attr('class', 'node-content')
            .attr('width', NODE_WIDTH)
            .attr('height', NODE_HEIGHT)
            .style('overflow', 'visible')      // CRITICAL for button visibility
            .style('pointer-events', 'all')    // CRITICAL for button clicks
            .append('xhtml:div')
            .style('pointer-events', 'all')    // CRITICAL for button clicks
            .html(d => renderNodeHTML(d));
    };

    const renderNodeHTML = (node) => {
        const hasUsers = node.data.active_users && node.data.active_users.length > 0;
        const userCount = node.data.active_users?.length || 0;
        const firstUser = hasUsers ? node.data.active_users[0] : null;
        const isHovered = hoveredNode === node.data.id;
        const nodeId = node.data.id;

        // Check if this node is in the highlighted path or is the selected position
        const isInPath = highlightedPath.includes(nodeId);
        const isSelected = nodeId === selectedPositionId;

        const statusBadge = hasUsers
            ? '<span class="p-tag p-tag-success" style="font-size: 0.8rem; padding: 0.3rem 0.6rem;">Filled</span>'
            : '<span class="p-tag p-tag-warning" style="font-size: 0.8rem; padding: 0.3rem 0.6rem;">Vacant</span>';

        const menuButton = isAdmin
            ? `<button class="p-button p-button-text p-button-rounded p-button-sm"
                       style="width: 28px; height: 28px;"
                       onclick="event.stopPropagation(); window.showNodeMenu(${node.data.id}, event)"
                       title="Actions">
                   <i class="pi pi-ellipsis-v"></i>
               </button>`
            : '';

        // Expand/collapse button for nodes with children or has_children flag (lazy loading)
        const childCount = node.data.children ? node.data.children.length : 0;
        const hasChildren = node.data.children && node.data.children.length > 0;
        const hasChildrenFlag = node.data.has_children;
        const shouldShowExpandButton = hasChildren || hasChildrenFlag;
        const isLoadingChildren = expandingPositionId === nodeId;

        // IMPORTANT: Only show "−" (minus) if node is expanded AND children are actually loaded
        // If children aren't loaded yet (empty array), show "+" even if node is in expandedNodes
        const isExpanded = expandedNodes.has(nodeId) && hasChildren;

        // Path highlighting: selected (blue pulse), path (cyan glow), normal
        let borderColor, bgColor, boxShadow;
        if (isSelected) {
            borderColor = '#3b82f6'; // Blue for selected
            bgColor = '#eff6ff'; // Light blue background
            boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.3), 0 4px 12px rgba(0,0,0,0.15)'; // Blue pulse
        } else if (isInPath) {
            borderColor = '#06b6d4'; // Cyan for path
            bgColor = '#ecfeff'; // Light cyan background
            boxShadow = '0 0 0 3px rgba(6, 182, 212, 0.25), 0 2px 8px rgba(0,0,0,0.12)'; // Cyan glow
        } else {
            borderColor = hasUsers ? '#22c55e' : '#fed7aa';
            bgColor = hasUsers ? '#f0fdf4' : '#fff7ed';
            boxShadow = '0 2px 6px rgba(0,0,0,0.12)';
        }

        const expandCollapseButton = shouldShowExpandButton ? `
            <button
                onclick="event.stopPropagation(); window.handleOrgChartNodeToggle(${nodeId}, ${hasChildrenFlag || false}, ${childCount})"
                style="
                    width: 32px;
                    height: 32px;
                    border-radius: 6px;
                    border: 2px solid ${borderColor};
                    background: ${borderColor};
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: ${isLoadingChildren ? 'wait' : 'pointer'};
                    padding: 0;
                    font-size: 1.2rem;
                    font-weight: bold;
                    line-height: 1;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                    position: relative;
                    z-index: 999;
                    pointer-events: all;
                "
                onmouseover="if (!${isLoadingChildren}) this.style.transform='scale(1.1)';"
                onmouseout="this.style.transform='scale(1)';"
                title="${isLoadingChildren ? 'Loading...' : (isExpanded ? 'Collapse' : 'Expand')}"
                ${isLoadingChildren ? 'disabled' : ''}
            >
                ${isLoadingChildren ? '<i class="pi pi-spin pi-spinner" style="font-size: 1rem;"></i>' : (isExpanded ? '−' : '+')}
            </button>
        ` : '';

        const avatar = hasUsers
            ? `<div style="width: 50px; height: 50px; border-radius: 50%; background: #22c55e; color: white; font-size: 1.4rem; font-weight: bold; display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                   ${(firstUser.name?.charAt(0) || 'U').toUpperCase()}
               </div>`
            : `<div style="width: 50px; height: 50px; border-radius: 50%; background: #fed7aa; color: #c2410c; font-size: 1.4rem; font-weight: bold; display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                   ?
               </div>`;

        const userName = hasUsers ? firstUser.name : 'No user assigned';

        const addChildButton = isAdmin && isHovered
            ? `<button class="p-button p-button-sm p-button-success p-button-outlined"
                       style="width: 100%; padding: 0.4rem 0.6rem; font-size: 0.8rem; margin-top: 8px;"
                       onclick="event.stopPropagation(); window.handleAddChildFromNode(${node.data.id})">
                   <i class="pi pi-plus"></i> Add Child Position
               </button>`
            : '';

        const childBadge = childCount > 0
            ? `<span class="p-badge p-badge-info" style="font-size: 0.75rem;">${childCount}</span>`
            : '';

        return `
            <div class="org-chart-node"
                 style="width: ${NODE_WIDTH}px;
                        height: ${NODE_HEIGHT}px;
                        background: ${bgColor};
                        border: 2px solid ${borderColor};
                        border-radius: 8px;
                        padding: 12px;
                        cursor: pointer;
                        box-shadow: ${boxShadow};
                        transition: all 0.2s ease;
                        pointer-events: all;
                        position: relative;"
                 onmouseenter="window.handleNodeHover(${node.data.id}, true)"
                 onmouseleave="window.handleNodeHover(${node.data.id}, false)"
                 onclick="event.stopPropagation(); window.handleNodeClick(${node.data.id})"
                 oncontextmenu="event.preventDefault(); event.stopPropagation(); window.showNodeMenu(${node.data.id}, event)">

                <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; gap: 8px; position: relative; z-index: 10;">
                    <div style="display: flex; align-items: center; gap: 8px; position: relative; z-index: 10;">
                        ${statusBadge}
                        ${expandCollapseButton}
                    </div>
                    ${menuButton}
                </div>

                <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 10px;">
                    ${avatar}
                    <div style="flex: 1; overflow: hidden;">
                        <h4 style="margin: 0; font-size: 1rem; font-weight: 600; color: #111827;
                                   white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                            ${node.data.position_name}
                        </h4>
                        <p style="margin: 4px 0 0 0; font-size: 0.85rem; color: #6b7280;
                                  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                            ${userName}
                        </p>
                    </div>
                </div>

                ${userCount > 1 ? `
                <div style="display: flex; align-items: center; gap: 4px; margin-bottom: 8px; font-size: 0.8rem; color: #6b7280;">
                    <i class="pi pi-users" style="font-size: 0.8rem;"></i>
                    <span>+${userCount - 1} more user${userCount > 2 ? 's' : ''}</span>
                </div>
                ` : ''}

                ${addChildButton}

                <div style="display: flex; align-items: center; justify-content: space-between; padding-top: 8px; border-top: 1px solid #e5e7eb; margin-top: 8px;">
                    <span style="font-size: 0.75rem; color: #9ca3af; display: flex; align-items: center; gap: 4px;">
                        <i class="pi pi-sitemap" style="font-size: 0.75rem;"></i>
                        Level ${node.data.level || 1}
                    </span>
                    ${childBadge}
                </div>
            </div>
        `;
    };

    // Setup window event handlers
    useEffect(() => {
        window.handleNodeClick = (nodeId) => {
            const node = findNodeById(nodeId);
            if (node && onPositionClick) {
                onPositionClick(node);
            }
        };

        window.handleNodeHover = (nodeId, isHovering) => {
            setHoveredNode(isHovering ? nodeId : null);
        };

        window.showNodeMenu = (nodeId, event) => {
            const node = findNodeById(nodeId);
            if (node) {
                setMenuItems(getContextMenuItems(node));
                if (contextMenuRef.current) {
                    contextMenuRef.current.show(event);
                }
            }
        };

        window.handleAddChildFromNode = (nodeId) => {
            const node = findNodeById(nodeId);
            if (node && onAddChild) {
                onAddChild(node);
            }
        };

        window.handleOrgChartNodeToggle = (nodeId, hasChildren, childCount) => {
            console.log('OrgChartTree: Toggle clicked for nodeId:', nodeId, 'hasChildren:', hasChildren, 'childCount:', childCount);
            // Construct minimal nodeData with current state (captured at render time)
            const nodeData = {
                has_children: hasChildren,
                children: childCount > 0 ? new Array(childCount) : []
            };
            toggleNodeExpand(nodeId, nodeData);
        };

        return () => {
            delete window.handleNodeClick;
            delete window.handleNodeHover;
            delete window.showNodeMenu;
            delete window.handleAddChildFromNode;
            delete window.handleOrgChartNodeToggle;
        };
    }, [treeData, onPositionClick, onAddChild, onAddSibling, onEditPosition, onDeletePosition, onAssignUser, onRemoveUser, isAdmin, toggleNodeExpand]);

    const handleZoomChange = (value) => {
        if (!zoomBehaviorRef.current || !svgRef.current) return;

        const svg = select(svgRef.current);
        const currentTransform = currentTransformRef.current || zoomIdentity;

        const newScale = value / 100;
        const centerX = dimensions.width / 2;
        const centerY = dimensions.height / 2;

        // Calculate the point in the SVG that's currently at the center of the viewport
        const pointX = (centerX - currentTransform.x) / currentTransform.k;
        const pointY = (centerY - currentTransform.y) / currentTransform.k;

        // Create new transform that keeps this point at the center while zooming
        const newTransform = zoomIdentity
            .translate(centerX, centerY)
            .scale(newScale)
            .translate(-pointX, -pointY);

        currentTransformRef.current = newTransform;
        setZoomLevel(value);
        svg.transition().duration(300).call(zoomBehaviorRef.current.transform, newTransform);
    };

    const handleZoomIn = () => {
        const newZoom = Math.min(200, zoomLevel + 10);
        handleZoomChange(newZoom);
    };

    const handleZoomOut = () => {
        const newZoom = Math.max(10, zoomLevel - 10);
        handleZoomChange(newZoom);
    };

    const handleFitToScreen = () => {
        if (!zoomBehaviorRef.current || !svgRef.current) return;

        const svg = select(svgRef.current);
        const initialScale = 0.8;
        const initialTransform = zoomIdentity
            .translate(dimensions.width * 0.05, 40)
            .scale(initialScale);

        currentTransformRef.current = initialTransform;
        setZoomLevel(Math.round(initialScale * 100));
        svg.transition()
            .duration(300)
            .call(zoomBehaviorRef.current.transform, initialTransform);
    };

    if (loading) {
        return (
            <div className="flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
                <i className="pi pi-spin pi-spinner text-4xl text-primary"></i>
            </div>
        );
    }

    if (!treeData || treeData.length === 0) {
        return (
            <Card className="text-center p-5">
                <i className="pi pi-sitemap text-6xl text-400 mb-3"></i>
                <h3 className="text-xl font-semibold text-600 mb-2">No Organizational Chart</h3>
                <p className="text-500 mb-0">
                    Create your first position to start building the organizational structure.
                </p>
            </Card>
        );
    }

    return (
        <div ref={containerRef} className="org-chart-container" style={{ width: '100%', height: '100%', position: 'relative', border: '1px solid #cbd5e1', borderRadius: '8px', overflow: 'hidden', background: '#f8fafc' }}>
            <Menu model={menuItems} popup ref={contextMenuRef} />

            <svg ref={svgRef} style={{ width: '100%', height: '100%', cursor: 'grab', touchAction: 'none' }}></svg>

            {/* Zoom Controls - Responsive */}
            <div className="org-chart-zoom-controls surface-card border-round shadow-2 p-2 flex align-items-center gap-2">
                <Button
                    icon="pi pi-minus"
                    className="p-button-sm p-button-text p-button-rounded"
                    onClick={handleZoomOut}
                    tooltip="Zoom Out"
                    tooltipOptions={{ position: 'top' }}
                    style={{ width: '28px', height: '28px', padding: 0 }}
                />

                <div className="org-chart-zoom-slider hidden md:flex align-items-center gap-2">
                    <Slider
                        value={zoomLevel}
                        onChange={(e) => handleZoomChange(e.value)}
                        min={10}
                        max={200}
                        step={5}
                        style={{ width: '80px' }}
                    />
                    <span className="text-xs font-medium text-600" style={{ minWidth: '38px', textAlign: 'right' }}>
                        {zoomLevel}%
                    </span>
                </div>

                <Button
                    icon="pi pi-plus"
                    className="p-button-sm p-button-text p-button-rounded"
                    onClick={handleZoomIn}
                    tooltip="Zoom In"
                    tooltipOptions={{ position: 'top' }}
                    style={{ width: '28px', height: '28px', padding: 0 }}
                />

                <div className="hidden md:block" style={{ width: '1px', height: '18px', background: '#e2e8f0', margin: '0 2px' }}></div>

                <Button
                    icon="pi pi-window-maximize"
                    className="p-button-sm p-button-text p-button-rounded"
                    onClick={handleFitToScreen}
                    tooltip="Fit to Screen"
                    tooltipOptions={{ position: 'top' }}
                    style={{ width: '28px', height: '28px', padding: 0 }}
                />
            </div>

            {/* Instructions - Hidden on mobile */}
            <div className="org-chart-instructions hidden md:block surface-card border-round shadow-2 p-2 md:p-3 text-sm text-600">
                <div className="mb-1"><i className="pi pi-arrows-alt mr-2"></i><strong>Pan:</strong> Drag canvas</div>
                <div><i className="pi pi-search mr-2"></i><strong>Zoom:</strong> Pinch or slider</div>
            </div>

            {/* Floating Search Panel - Responsive */}
            <div className="org-chart-search-wrapper">
                {!searchPanelOpen ? (
                    // Collapsed: Search Button
                    <Button
                        icon="pi pi-search"
                        className="p-button-rounded p-button-primary org-chart-search-btn"
                        onClick={toggleSearchPanel}
                        tooltip="Search Positions"
                        tooltipOptions={{ position: 'left' }}
                    />
                ) : (
                    // Expanded: Search Panel - Responsive
                    <div className="org-chart-search-panel surface-card border-round-xl shadow-4 p-3 md:p-4 flex flex-column gap-3 md:gap-4">
                        {/* Header */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <i className="pi pi-search" style={{ fontSize: '1.2rem', color: '#3b82f6' }}></i>
                                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#1e293b' }}>
                                    Search Positions
                                </h3>
                            </div>
                            <Button
                                icon="pi pi-times"
                                className="p-button-rounded p-button-text p-button-sm"
                                onClick={toggleSearchPanel}
                                style={{ width: '32px', height: '32px' }}
                            />
                        </div>

                        {/* Search Type Toggle */}
                        <div>
                            <SelectButton
                                value={searchType}
                                onChange={handleSearchTypeChange}
                                options={[
                                    { label: 'Position', value: 'position', icon: 'pi pi-sitemap' },
                                    { label: 'User', value: 'user', icon: 'pi pi-user' }
                                ]}
                                optionLabel="label"
                                optionValue="value"
                                style={{ width: '100%' }}
                            />
                            <small style={{ display: 'block', marginTop: '8px', color: '#64748b', fontSize: '0.8rem' }}>
                                {searchType === 'position' && 'Search by position name or code'}
                                {searchType === 'user' && 'Search by user name, email, or mobile'}
                            </small>
                        </div>

                        {/* Search Input - Using p-inputgroup for proper click handling */}
                        <div className="p-inputgroup">
                            <span className="p-inputgroup-addon">
                                <i className="pi pi-search"></i>
                            </span>
                            <InputText
                                value={searchQuery}
                                onChange={handleSearchQueryChange}
                                placeholder="Type at least 2 characters..."
                                className="w-full"
                                autoFocus
                            />
                            {searchQuery && (
                                <span
                                    className="p-inputgroup-addon cursor-pointer"
                                    onClick={() => {
                                        setSearchQuery('');
                                        setSearchResults([]);
                                    }}
                                >
                                    <i className="pi pi-times" style={{ color: '#94a3b8' }}></i>
                                </span>
                            )}
                        </div>

                        {/* Results Area */}
                        <div style={{
                            flex: 1,
                            overflowY: 'auto',
                            maxHeight: '400px',
                            minHeight: '100px'
                        }}>
                            {searchLoading && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '40px 0',
                                    flexDirection: 'column',
                                    gap: '12px'
                                }}>
                                    <ProgressSpinner style={{ width: '40px', height: '40px' }} />
                                    <span style={{ color: '#64748b', fontSize: '0.9rem' }}>Searching...</span>
                                </div>
                            )}

                            {!searchLoading && searchQuery.length < 2 && (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '60px 20px',
                                    color: '#94a3b8'
                                }}>
                                    <i className="pi pi-sitemap" style={{ fontSize: '3.5rem', marginBottom: '16px', display: 'block', color: '#cbd5e1' }}></i>
                                    <p style={{ margin: '0 0 8px 0', fontSize: '1rem', fontWeight: 600, color: '#64748b' }}>
                                        Start typing to search positions
                                    </p>
                                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#94a3b8' }}>
                                        Search by position name or assigned user
                                    </p>
                                </div>
                            )}

                            {!searchLoading && searchQuery.length >= 2 && searchResults.length === 0 && (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '60px 20px',
                                    color: '#94a3b8'
                                }}>
                                    <i className="pi pi-search" style={{ fontSize: '3.5rem', marginBottom: '16px', display: 'block', color: '#cbd5e1' }}></i>
                                    <p style={{ margin: '0 0 8px 0', fontSize: '1rem', fontWeight: 600, color: '#64748b' }}>
                                        No positions found matching "{searchQuery}"
                                    </p>
                                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#94a3b8' }}>
                                        Try searching with a different term
                                    </p>
                                </div>
                            )}

                            {!searchLoading && searchResults.length > 0 && (
                                <>
                                    <div style={{
                                        fontSize: '0.8rem',
                                        color: '#64748b',
                                        marginBottom: '12px',
                                        fontWeight: 500
                                    }}>
                                        Found {searchResults.length} result{searchResults.length > 1 ? 's' : ''}
                                    </div>
                                    {searchResults.map((position) => {
                                                        const hasUsers = position.assigned_users && position.assigned_users.length > 0;
                                                        const firstUser = hasUsers ? position.assigned_users[0] : null;
                                                        const avatarLetter = hasUsers ? firstUser.name.charAt(0).toUpperCase() : 'V';
                                                        const avatarColor = hasUsers ? '#22C55E' : '#F59E0B';

                                                        return (
                                                            <div
                                                                key={position.id}
                                                                onClick={() => handleSearchResultSelect(position)}
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '16px',
                                                                    padding: '16px',
                                                                    border: '1px solid #e2e8f0',
                                                                    borderRadius: '12px',
                                                                    marginBottom: '12px',
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s',
                                                                    background: 'white'
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.currentTarget.style.background = '#f8fafc';
                                                                    e.currentTarget.style.borderColor = '#3b82f6';
                                                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                                                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.15)';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.currentTarget.style.background = 'white';
                                                                    e.currentTarget.style.borderColor = '#e2e8f0';
                                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                                    e.currentTarget.style.boxShadow = 'none';
                                                                }}
                                                            >
                                                                {/* Avatar */}
                                                                <div style={{
                                                                    width: '56px',
                                                                    height: '56px',
                                                                    borderRadius: '50%',
                                                                    background: avatarColor,
                                                                    color: 'white',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    fontSize: '1.5rem',
                                                                    fontWeight: 'bold',
                                                                    flexShrink: 0,
                                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                                }}>
                                                                    {avatarLetter}
                                                                </div>

                                                                {/* Info */}
                                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                                    {/* Position Name and Code */}
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                                                        <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '1.05rem' }}>
                                                                            {position.position_name}
                                                                        </span>
                                                                        {position.position_code && (
                                                                            <span style={{
                                                                                background: '#f1f5f9',
                                                                                padding: '2px 8px',
                                                                                borderRadius: '4px',
                                                                                fontSize: '0.75rem',
                                                                                color: '#64748b',
                                                                                fontWeight: 500
                                                                            }}>
                                                                                {position.position_code}
                                                                            </span>
                                                                        )}
                                                                        <span style={{
                                                                            background: hasUsers ? '#dcfce7' : '#fef3c7',
                                                                            padding: '2px 8px',
                                                                            borderRadius: '4px',
                                                                            fontSize: '0.7rem',
                                                                            color: hasUsers ? '#16a34a' : '#ca8a04',
                                                                            fontWeight: 600
                                                                        }}>
                                                                            {hasUsers ? 'Filled' : 'Vacant'}
                                                                        </span>
                                                                    </div>

                                                                    {/* Assigned User Info */}
                                                                    {hasUsers && (
                                                                        <div style={{
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            gap: '4px',
                                                                            marginBottom: '8px'
                                                                        }}>
                                                                            <div style={{
                                                                                fontSize: '0.85rem',
                                                                                color: '#475569',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: '6px'
                                                                            }}>
                                                                                <i className="pi pi-user" style={{ fontSize: '0.75rem' }}></i>
                                                                                {firstUser.name}
                                                                            </div>
                                                                            {firstUser.email && (
                                                                                <div style={{
                                                                                    fontSize: '0.75rem',
                                                                                    color: '#64748b',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    gap: '6px'
                                                                                }}>
                                                                                    <i className="pi pi-envelope" style={{ fontSize: '0.7rem' }}></i>
                                                                                    {firstUser.email}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}

                                                                    {/* Level Info */}
                                                                    <div style={{
                                                                        fontSize: '0.75rem',
                                                                        color: '#94a3b8',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '4px'
                                                                    }}>
                                                                        <i className="pi pi-sitemap" style={{ fontSize: '0.7rem' }}></i>
                                                                        Level {position.level}
                                                                    </div>
                                                                </div>

                                                                {/* Stats */}
                                                                <div style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '16px',
                                                                    flexShrink: 0
                                                                }}>
                                                                    <div style={{ textAlign: 'center' }}>
                                                                        <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '4px' }}>
                                                                            Subordinates
                                                                        </div>
                                                                        <div style={{
                                                                            fontSize: '1.2rem',
                                                                            fontWeight: 'bold',
                                                                            color: '#3b82f6'
                                                                        }}>
                                                                            {position.subordinate_count || 0}
                                                                        </div>
                                                                    </div>
                                                                    <i className="pi pi-arrow-right" style={{ color: '#cbd5e1', fontSize: '0.9rem' }}></i>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <style>
                {`
                    .org-chart-container {
                        font-family: var(--font-family);
                    }

                    .org-chart-node:hover {
                        transform: translateY(-3px);
                        box-shadow: 0 6px 16px rgba(0,0,0,0.18) !important;
                    }

                    svg {
                        user-select: none;
                        -webkit-user-select: none;
                        -moz-user-select: none;
                    }

                    svg:active {
                        cursor: grabbing !important;
                    }

                    .org-chart-container * {
                        -webkit-user-drag: none;
                        -webkit-tap-highlight-color: transparent;
                    }

                    /* Slider customization */
                    .p-slider .p-slider-handle {
                        width: 18px;
                        height: 18px;
                        background: #3b82f6;
                        border: 2px solid white;
                    }

                    .p-slider .p-slider-range {
                        background: #3b82f6;
                    }

                    /* Responsive Controls */
                    .org-chart-zoom-controls {
                        position: absolute;
                        bottom: 12px;
                        right: 12px;
                        z-index: 100;
                    }

                    .org-chart-instructions {
                        position: absolute;
                        top: 12px;
                        left: 12px;
                        z-index: 100;
                        backdrop-filter: blur(4px);
                    }

                    .org-chart-search-wrapper {
                        position: absolute;
                        top: 12px;
                        right: 12px;
                        z-index: 1000;
                    }

                    .org-chart-search-btn {
                        width: 40px;
                        height: 40px;
                        font-size: 1rem;
                        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
                    }

                    .org-chart-search-panel {
                        width: calc(100vw - 24px);
                        max-width: 320px;
                        max-height: 60vh;
                        overflow-y: auto;
                    }

                    /* Desktop styles */
                    @media (min-width: 768px) {
                        .org-chart-zoom-controls {
                            bottom: 16px;
                            right: 16px;
                        }

                        .org-chart-instructions {
                            top: 20px;
                            left: 20px;
                        }

                        .org-chart-search-wrapper {
                            top: 20px;
                            right: 20px;
                        }

                        .org-chart-search-btn {
                            width: 48px;
                            height: 48px;
                            font-size: 1.2rem;
                        }

                        .org-chart-search-panel {
                            width: 480px;
                            max-width: 480px;
                            max-height: 70vh;
                        }
                    }
                `}
            </style>
        </div>
    );
};

export default OrgChartTree;
